/**
 * O que mudou entre duas fotografias — e o que só PARECE ter mudado.
 *
 * A ordem das seções não é a do processamento: **rolou** vem primeiro. Um título que saiu e outro
 * que entrou entre as mesmas partes é a mesma dívida empurrada, e essa leitura se perde se o
 * operador tiver de descobri-la sozinho lendo duas listas. Entrou/saiu/permaneceu vêm depois, para
 * quem quiser conferir a movimentação bruta — que continua intacta, porque a rolagem é uma leitura
 * A MAIS e não uma substituição.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Indicador } from '@/components/graficos';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';

type Parte = { rotulo: string; rota: string };

type TituloComp = {
  id: string;
  seu_numero: string | null;
  cedente: Parte;
  sacado: Parte;
  valor_nominal: number;
  venc_original: string | null;
  situacao: string | null;
};

type Permanencia = TituloComp & {
  valor_presente_antes: number;
  valor_presente_depois: number;
  delta: number;
};

type Comparacao = {
  de: { posicao_id: string; data: string };
  para: { posicao_id: string; data: string };
  resumo: {
    entraram: number;
    sairam: number;
    permaneceram: number;
    rolaram: number;
    valor_entrou: number;
    valor_saiu: number;
    valor_rolado: number;
  };
  entraram: TituloComp[];
  sairam: TituloComp[];
  permaneceram: Permanencia[];
  rolaram: { saiu: TituloComp; entrou: TituloComp; dias_adiante: number; variacao: number }[];
};

const reais = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const dia = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : '—');

function Titulo({ t }: { t: TituloComp }) {
  return (
    <span className="tipo-corpo-pequeno">
      <span className="tipo-dado text-text-secondary">{t.seu_numero || '—'}</span>{' '}
      <Link to={t.sacado.rota} className="text-accent hover:underline">
        {t.sacado.rotulo}
      </Link>{' '}
      <span className="tipo-dado text-text">{reais(t.valor_nominal)}</span>
      {t.venc_original && (
        <span className="text-text-tertiary"> · vence {dia(t.venc_original)}</span>
      )}
    </span>
  );
}

function Lista({ titulo, itens }: { titulo: string; itens: TituloComp[] }) {
  if (!itens.length) return null;
  return (
    <details className="border-t border-border pt-2">
      <summary className="tipo-rotulo cursor-pointer select-none text-text-secondary hover:text-text">
        {titulo} ({itens.length})
      </summary>
      <ul className="mt-2 flex flex-col gap-1">
        {itens.map((t) => (
          <li key={t.id}>
            <Titulo t={t} />
          </li>
        ))}
      </ul>
    </details>
  );
}

export function PainelComparacaoCarteira({ carteiraId }: { carteiraId: string }) {
  const [dados, setDados] = useState<Comparacao | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(async () => {
    setCarregando(true);
    // Sem parâmetros: as duas mais recentes. É a pergunta de nove em cada dez vezes, e pedir dois
    // identificadores para ela seria um formulário no caminho de uma resposta.
    const r = await api.get<Comparacao>(`/cadastro/carteiras/${carteiraId}/comparacao`);
    setCarregando(false);
    if (r.ok) {
      setDados(r.dados);
      setErro(null);
    } else {
      setErro(r.erro);
      setDados(null);
    }
  }, [carteiraId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (carregando) return <Spinner />;

  if (!dados) {
    return (
      <Card>
        <CardContent className="pt-6">
          {/* O motivo, e não um vazio: «uma fotografia só não conta história» é uma resposta. */}
          <p className="tipo-corpo-pequeno text-text-tertiary">
            {erro ?? 'Não foi possível comparar.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  const r = dados.resumo;
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          O que mudou — {dia(dados.de.data)} → {dia(dados.para.data)}
        </CardTitle>
        <p className="text-[13px] leading-[1.5] text-text-secondary">
          Comparação por título, não por linha de arquivo: é o mesmo papel de um mês para o outro,
          e não dois parecidos.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-x-8 gap-y-3">
          <Indicador
            rotulo="Entraram"
            valor={r.entraram}
            detalhe={reais(r.valor_entrou)}
          />
          <Indicador rotulo="Saíram" valor={r.sairam} detalhe={reais(r.valor_saiu)} />
          <Indicador rotulo="Permaneceram" valor={r.permaneceram} />
          <Indicador
            rotulo="Rolaram"
            valor={r.rolaram}
            detalhe={`${reais(r.valor_rolado)} empurrados`}
            alerta={r.rolaram > 0}
          />
        </div>

        {/* A leitura que se perde se o operador tiver de achá-la sozinho entre duas listas. */}
        {dados.rolaram.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="tipo-rotulo text-text-tertiary">
              A dívida não foi paga — foi empurrada
            </p>
            <div className="flex flex-col">
              {dados.rolaram.map((x) => (
                <div key={x.saiu.id} className="border-t border-border py-2 first:border-t-0">
                  <p className="tipo-corpo-pequeno text-text">
                    <Link to={x.saiu.cedente.rota} className="text-accent hover:underline">
                      {x.saiu.cedente.rotulo}
                    </Link>{' '}
                    contra{' '}
                    <Link to={x.saiu.sacado.rota} className="text-accent hover:underline">
                      {x.saiu.sacado.rotulo}
                    </Link>{' '}
                    — venceu {dia(x.saiu.venc_original)} e voltou para{' '}
                    <span className="font-semibold">{dia(x.entrou.venc_original)}</span> (
                    {x.dias_adiante} dias adiante)
                  </p>
                  <p className="tipo-corpo-pequeno text-text-tertiary">
                    {reais(x.saiu.valor_nominal)} → {reais(x.entrou.valor_nominal)}
                    {x.variacao !== 0 && (
                      <span className={x.variacao > 0 ? 'text-warning' : undefined}>
                        {' '}
                        ({x.variacao > 0 ? '+' : ''}
                        {reais(x.variacao)})
                      </span>
                    )}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <Lista titulo="Entraram" itens={dados.entraram} />
        <Lista titulo="Saíram" itens={dados.sairam} />

        {dados.permaneceram.length > 0 && (
          <details className="border-t border-border pt-2">
            <summary className="tipo-rotulo cursor-pointer select-none text-text-secondary hover:text-text">
              Permaneceram ({dados.permaneceram.length})
            </summary>
            <ul className="mt-2 flex flex-col gap-1">
              {dados.permaneceram.map((p) => (
                <li key={p.id} className="tipo-corpo-pequeno">
                  <Titulo t={p} />
                  {p.delta !== 0 && (
                    <span className={p.delta < 0 ? 'text-text-tertiary' : 'text-warning'}>
                      {' '}
                      · valor presente {p.delta > 0 ? '+' : ''}
                      {reais(p.delta)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}

        <div className="border-t border-border pt-3">
          <Button size="sm" variant="fantasma" onClick={() => void carregar()}>
            Recarregar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
